import type { App, NetworkId } from "@alch/dx-entities";
import { GetAppsListInput } from "@features/apps/useApps.js";
import { trpc } from "@util/trpc/trpcClient";

export const useUpdateAppNetworkAllowlist = ({
  scopeId,
}: {
  scopeId?: string;
} = {}) => {
  const utils = trpc.useUtils();
  const updateNetworkAllowlistCaches = ({
    appId,
    networksToAdd,
    networksToRemove,
    allowAllNetworks,
  }: NetworkAllowlistCacheUpdate) => {
    utils.apps.getApps.setData(GetAppsListInput, (apps) =>
      updateAppNetworkAllowlistInCache({
        apps,
        appId,
        networksToAdd,
        networksToRemove,
        allowAllNetworks,
      }),
    );
    utils.apps.getApp.setData({ appId }, (app) =>
      app
        ? updateSingleAppNetworkAllowlistInCache({
            app,
            networksToAdd,
            networksToRemove,
            allowAllNetworks,
          })
        : app,
    );
  };

  return trpc.apps.updateAppNetworkAllowlist.useMutation({
    scope: scopeId ? { id: scopeId } : undefined,
    onMutate: async ({ appId, networkIds, allowAllNetworks }) => {
      const nextAllowAllNetworks = allowAllNetworks ?? false;
      await Promise.all([
        utils.apps.getApps.cancel(GetAppsListInput),
        utils.apps.getApp.cancel({ appId }),
      ]);

      const apps = utils.apps.getApps.getData(GetAppsListInput);
      const app =
        apps?.find((app) => app.sid === appId) ??
        utils.apps.getApp.getData({ appId });

      if (!app) {
        return;
      }

      const networkAllowlist = app.networkAllowlist as string[];

      const networksToAdd = networkIds.filter(
        (id) => !networkAllowlist.includes(id),
      );
      const networksToRemove = networkAllowlist.filter(
        (id) => !networkIds.includes(id),
      );

      updateNetworkAllowlistCaches({
        appId,
        networksToAdd,
        networksToRemove,
        allowAllNetworks: nextAllowAllNetworks,
      });

      return {
        previousAllowAllNetworks: app.allowAllNetworks,
        networksToAdd,
        networksToRemove,
      };
    },
    onError: (_error, { appId }, context) => {
      if (!context) {
        return;
      }

      updateNetworkAllowlistCaches({
        appId,
        networksToAdd: context.networksToRemove,
        networksToRemove: context.networksToAdd,
        allowAllNetworks: context.previousAllowAllNetworks,
      });
    },
    onSettled: async (_data, _error, { appId }) => {
      await Promise.all([
        utils.apps.getApps.invalidate(GetAppsListInput),
        utils.apps.getApp.invalidate({ appId }),
      ]);
    },
  });
};

interface NetworkAllowlistCacheUpdate {
  appId: string;
  networksToAdd: string[];
  networksToRemove: string[];
  allowAllNetworks: boolean;
}

const updateAppNetworkAllowlistInCache = ({
  apps,
  appId,
  networksToAdd,
  networksToRemove,
  allowAllNetworks,
}: {
  apps: App[] | undefined;
  appId: string;
  networksToAdd: string[];
  networksToRemove: string[];
  allowAllNetworks: boolean;
}) => {
  return apps?.map((app) =>
    app.sid !== appId
      ? app
      : updateSingleAppNetworkAllowlistInCache({
          app,
          networksToAdd,
          networksToRemove,
          allowAllNetworks,
        }),
  );
};

const updateSingleAppNetworkAllowlistInCache = ({
  app,
  networksToAdd,
  networksToRemove,
  allowAllNetworks,
}: {
  app: App;
  networksToAdd: string[];
  networksToRemove: string[];
  allowAllNetworks: boolean;
}): App => {
  return {
    ...app,
    allowAllNetworks,
    networkAllowlist: [
      ...app.networkAllowlist.filter((id) => !networksToRemove.includes(id)),
      ...networksToAdd,
    ] as NetworkId[],
  };
};
